import { useState } from 'react'
import { api, errorText } from '../api/client'
import type { ContactProfile } from '../api/types'
import { BrandIcon } from './BrandIcon'
import { Icon } from './Icon'
import { Modal } from './Modal'
import { useToast } from './Toast'

// Where else you know someone. An officer looks a borrower up before calling, and
// an agent's LinkedIn is how a referral relationship gets checked — so these sit on
// the card rather than in a notes field.
export const SOCIAL_FIELDS = [
  { key: 'linkedin' as const, label: 'LinkedIn', brand: 'linkedin', hint: 'linkedin.com/in/… or a handle' },
  { key: 'x_handle' as const, label: 'X', brand: 'x', hint: '@handle' },
  { key: 'facebook' as const, label: 'Facebook', brand: 'facebook', hint: 'facebook.com/… or a handle' },
  { key: 'instagram' as const, label: 'Instagram', brand: 'instagram', hint: '@handle' },
  { key: 'website' as const, label: 'Website', brand: '', hint: 'example.com' },
]

export function SocialLinks({ contactId, profile, onSaved }: {
  contactId: number
  profile: ContactProfile | Record<string, never>
  onSaved: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const links = SOCIAL_FIELDS.map((f) => ({ ...f, url: (profile as Partial<ContactProfile>)[f.key] ?? null }))
  const present = links.filter((l) => l.url)

  function open() {
    setDraft(Object.fromEntries(SOCIAL_FIELDS.map((f) => [f.key, (profile as Partial<ContactProfile>)[f.key] ?? ''])))
    setEditing(true)
  }

  async function save() {
    setBusy(true)
    try {
      await api.saveProfile(contactId, Object.fromEntries(
        SOCIAL_FIELDS.map((f) => [f.key, draft[f.key]?.trim() || null])))
      toast('Saved', { tone: 'positive' })
      onSaved()
      setEditing(false)
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  /** "https://www.linkedin.com/in/dana" reads as "linkedin.com/in/dana", and an X
   *  profile reads as the handle, because that's how people say it. */
  function readable(field: string, url: string): string {
    const bare = url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '')
    if (field === 'x_handle') return `@${bare.split('/').pop()}`
    if (field === 'instagram') return `@${bare.split('/').pop()}`
    return bare
  }

  return (
    <>
      {present.map((l) => (
        <div className="kvrow" key={l.key}>
          <span className="k">{l.label}</span>
          <span className="v" style={{ minWidth: 0 }}>
            <a href={l.url!} target="_blank" rel="noopener noreferrer" className={`social-link s-${l.key}`}
              title={`${l.url} — opens in a new tab`}>
              {l.brand ? <BrandIcon name={l.brand} size={14} /> : <Icon name="language" size={14} />}
              <span>{readable(l.key, l.url!)}</span>
            </a>
          </span>
        </div>
      ))}
      <div className="kvrow">
        <span className="k">{present.length ? '' : 'Also on'}</span>
        <span className="v">
          <button className="linkbtn" onClick={open}>
            {present.length ? 'Edit profiles' : 'Add LinkedIn, X, a website…'}
          </button>
        </span>
      </div>

      {editing ? (
        <Modal open width={460} title="Where else you know them" onClose={() => setEditing(false)}
          footer={<>
            <span className="spacer" />
            <button className="btn" onClick={() => setEditing(false)}>Cancel</button>
            <button className="btn primary" disabled={busy} onClick={save}>Save</button>
          </>}>
          <div className="stack">
            {SOCIAL_FIELDS.map((f) => (
              <label key={f.key} className="fl">
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {f.brand ? <BrandIcon name={f.brand} size={13} /> : <Icon name="language" size={13} />}
                  {f.label} <span className="hint">— {f.hint}</span>
                </span>
                <input className="field" value={draft[f.key] ?? ''} placeholder={f.hint}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))} />
              </label>
            ))}
          </div>
        </Modal>
      ) : null}
    </>
  )
}
